// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useGetBurnChoices } from '@hooks/mutations/registry/burn/useGetBurnChoices';
import { useGetMintChoices } from '@hooks/mutations/registry/mint/useGetMintChoices';
import { useGetTransferChoices } from '@hooks/mutations/registry/transferInstruction/useGetTransferChoices';
import { ButtonOwnProps } from '@mui/material';
import { useParties } from '../../contexts/PartiesContext';
import { ChoiceDialogProps } from '../ChoiceDialog';
import { RejectDialog } from '../Dialog/RejectDialog';
import { RequestState, RequestType, RowNew } from './types';

export const useCreateChoices = (onClose: () => void, selectedRow?: RowNew) => {
  const { party } = useParties();
  const isSender = party === selectedRow?.sender;
  const isReceiver = party === selectedRow?.receiver;
  const mintChoices = useGetMintChoices(onClose, selectedRow);
  const burnChoices = useGetBurnChoices(onClose, selectedRow);
  const transferChoices = useGetTransferChoices(onClose, selectedRow);
  const actions: ChoiceDialogProps['actions'] = [];

  const choiceMap = {
    [RequestType.Transfer]: {
      sender: [
        {
          name: 'cancel',
          color: 'error' as ButtonOwnProps['color'],
          action: transferChoices.onCancel,
        },
      ],
      receiver: [
        {
          name: 'accept',
          action: transferChoices.onAccept,
        },
        {
          name: 'reject',
          color: 'error' as ButtonOwnProps['color'],
          form: (
            <RejectDialog
              open
              disableDialog
              onClose={onClose}
              onReject={(reason: string) => transferChoices.onReject(reason)}
            />
          ),
        },
      ],
    },
    [RequestType.Burn]: {
      sender: [
        {
          name: 'Cancel',
          color: 'error' as ButtonOwnProps['color'],
          action: burnChoices.onCancel,
        },
      ],
      receiver: [
        {
          name: 'Accept',
          action: burnChoices.onAccept,
        },
        {
          name: 'Reject',
          color: 'error' as ButtonOwnProps['color'],
          form: (
            <RejectDialog
              open
              disableDialog
              onReject={(reason: string) => burnChoices.onReject(reason)}
              onClose={onClose}
            />
          ),
        },
      ],
    },
    [RequestType.Mint]: {
      sender: [
        {
          name: 'Cancel',
          color: 'error' as ButtonOwnProps['color'],
          action: mintChoices.onCancel,
        },
      ],
      receiver: [
        {
          name: 'Accept',
          action: mintChoices.onAccept,
        },
        {
          name: 'Reject',
          color: 'error' as ButtonOwnProps['color'],
          form: (
            <RejectDialog
              open
              disableDialog
              onReject={(reason: string) => mintChoices.onReject(reason)}
              onClose={onClose}
            />
          ),
        },
      ],
    },
  };
  if (
    selectedRow?.requestState === RequestState.Offered ||
    selectedRow?.requestState === RequestState.Requested
  ) {
    if (isReceiver) {
      actions.push(...choiceMap[selectedRow.requestType].receiver);
    }
    if (isSender) {
      actions.push(...choiceMap[selectedRow.requestType].sender);
    }
  }
  return actions;
};
